import { getClosestHandle, getHandleCandidates, getHandlePoint, pointToFlow, Position, toConnection } from '../../core';
import type { HandleCandidate, HandleRef, XYPosition } from '../../core';
import type { MindmapStoreApi } from '../../store';
import { RENDERER_SELECTOR, rendererPoint } from '../../utils';
import { startAutoPan } from '../autoPan';
import { handleUnderPointer, isValidEnd } from './handleDom';

const sameHandle = (a: HandleRef, b: HandleRef) => a.nodeId === b.nodeId && a.handleId === b.handleId && a.type === b.type;

/** Where the dragged connection starts: the middle of the start handle's outer side. */
const startOf = (store: MindmapStoreApi, from: HandleRef, pointer: XYPosition) => {
  const internal = store.getState().nodeLookup.get(from.nodeId);
  const bound = internal?.handleBounds?.[from.type].find((handle) => handle.id === from.handleId);
  if (!internal || !bound) return { fromPoint: pointer, fromPosition: Position.Top };
  return { fromPoint: getHandlePoint(bound, internal.positionAbsolute), fromPosition: bound.position };
};

/**
 * Drags a new connection from a handle (v11 behaviour): the line follows the pointer and snaps to a valid handle
 * under the pointer or within `connectionRadius`; releasing on a valid handle calls `onConnect`. Near the pane edge
 * the view auto-pans (v11 `autoPanOnConnect`). Document-level listeners (no pointer capture) so the click after the
 * drag does not land on the start handle.
 */
export const startConnectionDrag = (store: MindmapStoreApi, event: PointerEvent, handleElement: HTMLElement, from: HandleRef): void => {
  const doc = handleElement.ownerDocument;
  const renderer = handleElement.closest(RENDERER_SELECTOR);
  if (!renderer) return;
  const toFlow = (pointerEvent: MouseEvent) => pointToFlow(rendererPoint(renderer, pointerEvent), store.getState().viewport);
  const { nodeLookup, connectHandlers } = store.getState();
  const candidates = getHandleCandidates(
    Array.from(nodeLookup.values()).map(({ node, positionAbsolute, handleBounds }) => ({ id: node.id, positionAbsolute, handleBounds })),
    from,
  );
  const isValid = (to: HandleRef) => isValidEnd(doc, from, to, store.getState().connectionMode);
  const centreOf = (to: HandleRef): HandleCandidate | undefined => candidates.find((candidate) => sameHandle(candidate, to));
  const pointer = toFlow(event);
  store.getState().setConnection({ from, ...startOf(store, from, pointer), toPoint: pointer, endHandle: null, isValid: false });
  connectHandlers.onConnectStart?.(event, { nodeId: from.nodeId, handleId: from.handleId, handleType: from.type });

  const updateLine = (moveEvent: PointerEvent) => {
    const current = store.getState().connection;
    if (!current) return;
    const point = toFlow(moveEvent);
    const below = handleUnderPointer(doc, moveEvent);
    const end = below && isValid(below) ? below : getClosestHandle(point, candidates, store.getState().connectOptions.connectionRadius, isValid);
    const valid = Boolean(end && isValid(end));
    const snapTo = end && valid ? centreOf(end) : undefined;
    store.getState().setConnection({ ...current, toPoint: snapTo ?? point, endHandle: end, isValid: valid });
  };
  // Started on press like v11; after each pan the line end is placed under the pointer again.
  const autoPan = startAutoPan(store, renderer, updateLine);
  autoPan.update(event);

  const handleMove = (moveEvent: PointerEvent) => {
    autoPan.update(moveEvent);
    updateLine(moveEvent);
  };

  const handleUp = (upEvent: PointerEvent) => {
    const current = store.getState().connection;
    autoPan.stop();
    doc.removeEventListener('pointermove', handleMove);
    doc.removeEventListener('pointerup', handleUp);
    const { setConnection, connectHandlers: handlers } = store.getState();
    if (current?.endHandle && current.isValid) handlers.onConnect?.(toConnection(from, current.endHandle));
    handlers.onConnectEnd?.(upEvent);
    setConnection(null);
  };

  doc.addEventListener('pointermove', handleMove);
  doc.addEventListener('pointerup', handleUp);
};
